import type { ReactNode } from "react";

import { cn } from "../../../lib/utils.js";
import { GlowCard } from "../glow-card.js";

export interface ChartPanelProps {
  title: ReactNode;
  /** One short line saying what the chart answers. */
  hint?: ReactNode;
  /** Right-aligned control, e.g. a dimension switch. */
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}

/**
 * A titled container for one chart, so a row of charts reads as one system.
 *
 * It sits on a `GlowCard` at the lightest setting -- `glow="ultra"` lifts only the
 * edge nearest the cursor, with no bloom to compete with the chart marks, and
 * `spotlight="off"` because the interior hover pool washed across the slices, bars
 * and plot area of whatever chart the panel holds. The edge glow is enough to show
 * the panel reacts; the marks keep their own colours.
 */
export function ChartPanel({ title, hint, action, children, className }: ChartPanelProps) {
  return (
    <GlowCard
      glow="ultra"
      spotlight="off"
      size="none"
      className={cn("flex min-w-0 flex-col gap-3 p-4", className)}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-[0.5px] text-surface-500">{title}</div>
          {hint && <div className="mt-0.5 truncate text-[11px] text-surface-400">{hint}</div>}
        </div>
        {action}
      </div>
      {children}
    </GlowCard>
  );
}
